import { Injectable, inject, runInInjectionContext, Injector, signal, computed, OnDestroy } from '@angular/core';
import { Firestore, doc, setDoc, getDoc, onSnapshot, serverTimestamp, DocumentReference, Unsubscribe } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, BehaviorSubject, switchMap, of, catchError, take } from 'rxjs';
import { User } from '@angular/fire/auth';
import { UserDocument, CreateUserDocumentData } from '../models/user-document.interface';

@Injectable({
	providedIn: 'root'
})
export class UserService implements OnDestroy {
	private firestore = inject(Firestore);
	private functions = inject(Functions);
	private injector = inject(Injector);
	private unsubscribe?: Unsubscribe;

	// Signals for reactive state management
	public currentUser = signal<UserDocument | null>(null);
	public isLoading = signal<boolean>(false);
	public error = signal<string | null>(null);

	// Computed signals
	public isUserLoaded = computed(() => this.currentUser() !== null);
	public userScore = computed(() => this.currentUser()?.score || null);
	public dailyPlays = computed(() => this.currentUser()?.dailyPlays || null);
	public decisions = computed(() => this.currentUser()?.decisions || null);
	public prizeWins = computed(() => this.currentUser()?.prizeWins || { log: [] });
	public email = computed(() => this.currentUser()?.email || null);

	// Private subjects for internal state management
	private userSubject = new BehaviorSubject<User | null>(null);
	private userDocumentSubject = new BehaviorSubject<UserDocument | null>(null);

	constructor() {
		// Set up user document listener when user changes
		this.userSubject.pipe(
			switchMap(user => {
				if (!user) {
					this.currentUser.set(null);
					this.userDocumentSubject.next(null);
					// Unsubscribe from previous listener
					if (this.unsubscribe) {
						this.unsubscribe();
						this.unsubscribe = undefined;
					}
					return of(null);
				}
				return this.listenToUserDocument(user.uid);
			}),
			catchError(error => {
				console.error('Error in user document stream:', error);
				this.error.set('Failed to load user data');
				return of(null);
			})
		).subscribe(userDoc => {
			this.currentUser.set(userDoc);
			this.userDocumentSubject.next(userDoc);
		});
	}

	/**
	 * Set the current authenticated user to start listening to their document
	 */
	setCurrentUser(user: User | null): void {
		this.userSubject.next(user);
	}

	/**
	 * Listen to user document changes in Firestore using onSnapshot
	 */
	private listenToUserDocument(userId: string): Observable<UserDocument | null> {
		return new Observable<UserDocument | null>(observer => {
			// Unsubscribe from previous listener if exists
			if (this.unsubscribe) {
				this.unsubscribe();
			}

			this.unsubscribe = runInInjectionContext(this.injector, () => {
				const userDocRef = doc(this.firestore, 'users', userId);
				return onSnapshot(
					userDocRef,
					(docSnapshot) => {
						if (docSnapshot.exists()) {
							const userData = docSnapshot.data() as UserDocument;
							observer.next(userData);
						} else {
							observer.next(null);
						}
					},
					(error) => {
						console.error('Error listening to user document:', error);
						observer.error(error);
					}
				);
			});
		});
	}

	/**
	 * Get user document reference
	 */
	private getUserDocumentRef(userId: string): DocumentReference {
		return runInInjectionContext(this.injector, () => {
			return doc(this.firestore, 'users', userId);
		});
	}

	/**
	 * Create a new user document if it doesn't exist
	 * This is only used for initial document creation - all updates happen via cloud functions
	 */
	async createUserDocument(userData: CreateUserDocumentData): Promise<void> {
		try {
			this.isLoading.set(true);
			this.error.set(null);

			// Check if document already exists
			const existingDoc = await this.getUserDocument(userData.userId);
			if (existingDoc) {
				return;
			}

			// Create new user document with default values
			// Note: All updates after creation will be handled by cloud functions
			const newUserDoc: UserDocument = {
				userId: userData.userId,
				email: userData.email,
				firstName: userData.firstName || '',
				createdAt: serverTimestamp(),
				score: {
					totalCash: 0,
					totalCashRank: 0,
					totalHappiness: 0,
					log: []
				},
				dailyPlays: {
					lastPlayDate: null,
					playsByDate: {}
				},
				decisions: {
					avatarTags: {
						relationship: '',
						lifestyle: '',
						career: ''
					},
					decisionPath: null,
					nextDecision: 1,
					log: []
				},
				prizeWins: {
					log: []
				}
			};

			await runInInjectionContext(this.injector, async () => {
				const userDocRef = doc(this.firestore, 'users', userData.userId);
				await setDoc(userDocRef, newUserDoc);
			});
		} catch (error: any) {
			console.error('Error creating user document:', error);
			this.error.set('Failed to create user profile');
			throw error;
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Get user document by ID (one-time read) using getDoc
	 */
	async getUserDocument(userId: string): Promise<UserDocument | null> {
		try {
			const docSnapshot = await runInInjectionContext(this.injector, async () => {
				const userDocRef = doc(this.firestore, 'users', userId);
				return await getDoc(userDocRef);
			});
			
			if (docSnapshot.exists()) {
				return docSnapshot.data() as UserDocument;
			} else {
				return null;
			}
		} catch (error) {
			console.error('Error getting user document:', error);
			return null;
		}
	}


	/**
	 * Clear current user data
	 */
	clearUserData(): void {
		// Unsubscribe from current listener
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = undefined;
		}
		
		this.currentUser.set(null);
		this.userSubject.next(null);
		this.userDocumentSubject.next(null);
		this.error.set(null);
	}

	/**
	 * Cleanup on service destruction
	 */
	ngOnDestroy(): void {
		if (this.unsubscribe) {
			this.unsubscribe();
		}
	}

	/**
	 * Get user document as observable
	 */
	getUserDocument$(): Observable<UserDocument | null> {
		return this.userDocumentSubject.asObservable();
	}

	/**
	 * Get today's date in AEST timezone (YYYY-MM-DD format)
	 * This method works regardless of the client's local timezone
	 */
	getTodayAESTDate(): string {
		const now = new Date();
		
		// Use Intl.DateTimeFormat to get the date in AEST timezone
		// This works regardless of the client's local timezone
		const formatter = new Intl.DateTimeFormat('en-CA', {
			timeZone: 'Australia/Sydney', // This handles both AEST and AEDT automatically
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		});
		
		const parts = formatter.formatToParts(now);
		const year = parts.find(part => part.type === 'year')?.value;
		const month = parts.find(part => part.type === 'month')?.value;
		const day = parts.find(part => part.type === 'day')?.value;
		
		return `${year}-${month}-${day}`;
	}

	/**
	 * Check if today's daily play session exists
	 */
	hasTodayPlaySession(): boolean {
		const user = this.currentUser();
		if (!user || !user.dailyPlays || !user.dailyPlays.playsByDate) {
			return false;
		}
		
		const todayDate = this.getTodayAESTDate();
		return !!user.dailyPlays.playsByDate[todayDate];
	}

	/**
	 * Initialize daily play session for today's AEST date
	 * This method calls the cloud function to create a new daily play session
	 * Note: The server validates that the date is actually today's AEST date for security
	 */
	async initializeDailyPlaySession(): Promise<{ success: boolean; message: string; dateString?: string }> {
		try {
			this.isLoading.set(true);
			this.error.set(null);

			const todayDate = this.getTodayAESTDate();
			const currentUser = this.currentUser();
			
			if (!currentUser) {
				throw new Error('No authenticated user found');
			}

			// Check if today's session already exists
			if (this.hasTodayPlaySession()) {
				return {
					success: true,
					message: 'Daily play session already exists for today',
					dateString: todayDate
				};
			}

			// Call the cloud function with today's date
			// The server will validate that this is actually today's AEST date
			const initializeDailyPlay = runInInjectionContext(this.injector, () => {
				return httpsCallable(this.functions, 'initializeDailyPlaySession');
			});
			const result = await initializeDailyPlay({
				userId: currentUser.userId,
				dateString: todayDate
			});

			const data = result.data as { success: boolean; message: string; dateString: string };
			
			return data;

		} catch (error: any) {
			console.error('Error initializing daily play session:', error);
			
			// Handle specific Firebase function errors
			let errorMessage = 'Failed to initialize daily play session';
			if (error.code === 'functions/invalid-argument') {
				errorMessage = error.message || 'Invalid date provided - only today\'s date can be initialized';
			} else if (error.code === 'functions/not-found') {
				errorMessage = 'User not found - please try logging in again';
			} else if (error.code === 'functions/failed-precondition') {
				errorMessage = 'User data structure is invalid - please contact support';
			} else if (error.message) {
				errorMessage = error.message;
			}
			
			this.error.set(errorMessage);
			return {
				success: false,
				message: errorMessage
			};
		} finally {
			this.isLoading.set(false);
		}
	}
}

